/**
 * The questions a QR code asks, for whichever kind it is.
 *
 * One component for both places a code is written - the Elements panel, where
 * it is added, and the settings panel, where it is changed - because two forms
 * for the same thing are two chances for one of them to forget a field.
 */

import { useId } from 'react';
import {
  QR_TYPES,
  fieldValue,
  phoneNumber,
  qrProblems,
  qrType,
  type QrFieldValues,
  type QrKind,
} from '../codes/qr-types';

/** The kinds, grouped the way a customer thinks about them. */
export function QrKindSelect( { value, onChange }: { value: QrKind; onChange: ( kind: QrKind ) => void } ) {
  const groups = [ 'Links', 'Contact', 'Other' ] as const;

  return (
    <label className="tb-field">
      <span className="tb-field__label">Holds</span>
      <select className="tb-input" data-qr-kind value={ value } onChange={ ( event ) => onChange( event.target.value as QrKind ) }>
        { groups.map( ( group ) => (
          <optgroup key={ group } label={ group }>
            { QR_TYPES.filter( ( t ) => t.group === group ).map( ( t ) => (
              <option key={ t.kind } value={ t.kind }>
                { t.label }
              </option>
            ) ) }
          </optgroup>
        ) ) }
      </select>
    </label>
  );
}

interface Props {
  kind: QrKind;
  fields: QrFieldValues;
  onChange: ( fields: QrFieldValues ) => void;
  /** Enter in a one-line field: add the code, where there is an add button. */
  onSubmit?: () => void;
  onCancel?: () => void;
  autoFocus?: boolean;
}

export function QrFields( { kind, fields, onChange, onSubmit, onCancel, autoFocus }: Props ) {
  const type = qrType( kind );
  const id = useId();
  const problems = qrProblems( kind, fields );

  const set = ( key: string, value: string ) => onChange( { ...fields, [ key ]: value } );

  const keys = ( event: React.KeyboardEvent, multiline: boolean ) => {
    if ( event.key === 'Enter' && ! multiline && onSubmit ) {
      event.preventDefault();
      onSubmit();
    }

    if ( event.key === 'Escape' && onCancel ) {
      event.preventDefault();
      onCancel();
    }
  };

  return (
    <div className="tb-qr-fields" data-qr-form={ kind }>
      { type.fields.map( ( field, index ) => {
        const value = fields[ field.key ] ?? '';
        const shown = fieldValue( type, fields, field.key );
        const inputId = `${ id }-${ field.key }`;
        const focus = autoFocus && index === 0;

        if ( field.input === 'toggle' ) {
          return (
            <label key={ field.key } className="tb-check">
              <input
                type="checkbox"
                data-qr-field={ field.key }
                checked={ value === 'yes' }
                onChange={ ( event ) => set( field.key, event.target.checked ? 'yes' : '' ) }
              />
              <span>{ field.label }</span>
            </label>
          );
        }

        const label = (
          <span className="tb-field__label">
            { field.label }
            { field.required && <span className="tb-qr-fields__req" aria-hidden="true"> *</span> }
          </span>
        );

        if ( field.input === 'select' ) {
          return (
            <label key={ field.key } className="tb-field">
              { label }
              <select className="tb-input" data-qr-field={ field.key } value={ shown } onChange={ ( event ) => set( field.key, event.target.value ) }>
                { field.options?.map( ( option ) => (
                  <option key={ option.value } value={ option.value }>
                    { option.label }
                  </option>
                ) ) }
              </select>
            </label>
          );
        }

        if ( field.input === 'textarea' ) {
          return (
            <label key={ field.key } className="tb-field">
              { label }
              <textarea
                className="tb-input tb-input--tall"
                data-qr-field={ field.key }
                rows={ 2 }
                value={ value }
                placeholder={ field.placeholder }
                autoFocus={ focus }
                onChange={ ( event ) => set( field.key, event.target.value ) }
                onKeyDown={ ( event ) => keys( event, true ) }
              />
            </label>
          );
        }

        // What a phone will actually dial, when that is not what was typed:
        // eight digits become a Qatari number, and a customer abroad needs to
        // see that theirs did not.
        const dials = field.input === 'tel' ? phoneNumber( value ) : '';

        return (
          <label key={ field.key } className="tb-field" htmlFor={ inputId }>
            { label }
            <input
              id={ inputId }
              className="tb-input"
              data-qr-field={ field.key }
              type={ field.input === 'datetime' ? 'datetime-local' : field.input === 'url' ? 'text' : field.input }
              inputMode={ field.input === 'url' ? 'url' : undefined }
              value={ value }
              placeholder={ field.placeholder }
              autoFocus={ focus }
              autoComplete="off"
              onChange={ ( event ) => set( field.key, event.target.value ) }
              onKeyDown={ ( event ) => keys( event, false ) }
            />
            { dials && dials.replace( /\D/g, '' ) !== value.replace( /\D/g, '' ) && (
              <small className="tb-qr-fields__note" data-qr-dials={ field.key }>
                Scans as { dials }
              </small>
            ) }
          </label>
        );
      } ) }

      { type.fields.some( ( f ) => f.input === 'tel' ) && (
        <p className="tb-qr-fields__hint">Qatar numbers can be typed as they are. Numbers from elsewhere need their country code, like +91.</p>
      ) }

      { problems.map( ( problem ) => (
        <p key={ problem } className="tb-qr-fields__problem" role="status">
          { problem }
        </p>
      ) ) }
    </div>
  );
}
